/* global Stimulus */
import { Controller } from "@hotwired/stimulus";
import sendSentryError from "../common/sentry_error/sentry_error";
import { DirectUpload } from "@rails/activestorage";

export default class extends Controller {
  static targets = [
    "startButton",
    "stopButton",
    "nextButton",
    "video",
    "testVideo",
    "recording",
    "canvas",
    "errorMessageTitle",
    "errorMessage",
    "errorMessageIcon",
    "overlay",
    "recordingStatus",
    "recordingTimer",
    "timerDisplay",
  ];

  connect() {
    this.recordingStartTime = null;
    this.timerInterval = null;

    if (this.hasVideoTarget && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      this.getCameraStream("environment", this.videoTarget);
    }

    if (this.hasTestVideoTarget && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      this.getCameraStream("user", this.testVideoTarget);
    }

    if (this.hasRecordingTarget && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      this.getCameraStream("user", this.recordingTargets).then((mediaRecorder) => {
        this.mediaRecorder = mediaRecorder;
        this.handleMediaRecorder();
      });
    }

    if (this.hasNextButtonTarget && this.nextButtonTarget.hasAttribute("disabled")) {
      this.toggleButton(this.nextButtonTarget);
    }
  }

  navigationWarning() {
    this.preventUnloadHandler = (event) => {
      event.preventDefault();
      event.returnValue = true; // deprecated, but without this chrome doesn't show the warning https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent/returnValue
    };
    window.addEventListener("beforeunload", this.preventUnloadHandler);
  }

  removeNavigationWarning() {
    if (this.preventUnloadHandler) {
      window.removeEventListener("beforeunload", this.preventUnloadHandler);
      this.preventUnloadHandler = null;
    }
  }

  startTimer() {
    this.recordingStartTime = Date.now();
    this.updateTimer();

    if (this.hasRecordingStatusTarget) {
      this.recordingStatusTarget.classList.remove("hidden");
    }
    if (this.hasRecordingTimerTarget) {
      this.recordingTimerTarget.classList.remove("hidden");
    }

    this.timerInterval = setInterval(() => {
      this.updateTimer();
    }, 1000);
  }

  stopTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    if (this.hasRecordingStatusTarget) {
      this.recordingStatusTarget.classList.add("hidden");
    }
    if (this.hasRecordingTimerTarget) {
      this.recordingTimerTarget.classList.add("hidden");
    }

    this.recordingStartTime = null;
  }

  updateTimer() {
    if (!this.recordingStartTime || !this.hasTimerDisplayTarget) return;

    const elapsed = Math.floor((Date.now() - this.recordingStartTime) / 1000);
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;

    const timeString = `${minutes}:${seconds.toString().padStart(2, "0")}`;
    this.timerDisplayTarget.textContent = timeString;
  }

  getCameraStream(facingMode, targets) {
    // Lower resolution and frame rate for smaller video size
    let constraints = {
      video: {
        width: { ideal: 640, max: 640 },
        height: { ideal: 480, max: 480 },
        frameRate: { ideal: 15, max: 20 },
      },
      audio: false,
    };

    if (this.isMobileDevice() && facingMode) {
      constraints.video.facingMode = facingMode;
    }

    return navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        this.cameraStream = stream;

        if (!(targets instanceof Array)) {
          targets = [targets];
        }

        targets.forEach((target) => {
          target.srcObject = stream;
          target.play();
        });

        if (this.hasOverlayTarget) {
          this.overlayTarget.classList.remove("hidden");
        }

        return new MediaRecorder(stream);
      })
      .catch((error) => {
        this.handleCameraError(error);
      });
  }

  handleCameraError(error) {
    const errorMessageMap = {
      NotAllowedError: [
        "Grant Permissions",
        "Camera access was denied. Please grant permissions and try again.",
      ],
      NotFoundError: [
        "No Camera Found",
        "No camera device was found. Please ensure your camera is connected and working.",
      ],
      NotSupportedError: [
        "Browser Not Supported",
        "Your browser doesn't support camera access. Please try with a different browser.",
      ],
      AbortError: ["Camera Access Aborted", "Camera access was aborted. Please try again."],
    };

    const [errorMessageTitle, errorMessage] = errorMessageMap[error.name] || [
      "An Error Occurred",
      `An error occurred while accessing the camera. (${error.name}) Please try again later.`,
    ];

    this.displayError(errorMessageTitle, errorMessage);
    sendSentryError("Stat dec: " + error);
  }

  displayError(title, message) {
    this.errorMessageTitleTarget.textContent = title;
    this.errorMessageTitleTarget.classList.remove("hidden");
    this.errorMessageTarget.textContent = message;
    this.errorMessageIconTarget.classList.remove("hidden");
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden");
    }
  }

  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
  }



  uploadData(endpoint, data) {
    return fetch(
      "/next_of_kin/statutory_declaration/" + this.data.get("funeralidValue") + `/${endpoint}`,
      {
        method: "PATCH",
        body: data,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      },
    )
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        console.log("Success:", data);
      })
      .catch((error) => {
        console.error("Error:", error);
        throw error;
      });
  }

  handleMediaRecorder() {
    this.chunks = [];
    this.mediaRecorder.start();

    // Start the timer when recording begins
    this.startTimer();

    this.mediaRecorder.ondataavailable = (event) => {
      this.chunks.push(event.data);
    };

    this.mediaRecorder.onstop = () => {
      // Stop the timer when recording stops
      this.stopTimer();
      this.navigationWarning();

      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach((track) => track.stop());
      }

      const blob = new Blob(this.chunks, { type: "video/mp4" });
      // Give the blob a generic filename for ActiveStorage
      const file = new File([blob], "statutory_declaration_video.mp4", { type: "video/mp4" });
      // Use DirectUpload for the video file
      const upload = new DirectUpload(
        file,
        "/rails/active_storage/direct_uploads?attachment=statutory_declaration[declaration_video]",
        this,
      );
      upload.create((error, blobUpload) => {
        if (error) {
          this.removeNavigationWarning();
          window.HSOverlay.close("#modal-loading");
          this.displayError("Upload Error", "Failed to upload video. Please try again.");
          return;
        }
        const formData = new FormData();
        formData.append("video_and_signature_recordings[declaration_video]", blobUpload.signed_id);

        if (this.flowFinished) {
          this.getSignaturePad()
            .getSignatureData()
            .then(this.blobToBase64)
            .then((base64String) => {
              formData.append("video_and_signature_recordings[signature_blob]", base64String);

              this.uploadData("upload_video_and_signature", formData)
                .then(() => {
                  this.removeNavigationWarning();
                  window.location.href =
                    "/next_of_kin/statutory_declaration/success/" + this.data.get("funeralidValue");
                })
                .catch((error) => {
                  console.error("Error:", error);
                  this.removeNavigationWarning();
                  window.HSOverlay.close("#modal-loading");
                  this.displayError("Upload Failed", "Failed to submit your statutory declaration. Please try again.");
                });
            });
        }
      });
    };
  }

  handleStopMediaRecorder() {
    this.flowFinished = true;
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
    }
    // Timer will be stopped in the onstop event handler
  }

  dataURItoBlob(dataURI) {
    // convert base64/URLEncoded data component to raw binary data held in a string
    var byteString;
    if (dataURI.split(",")[0].indexOf("base64") >= 0) byteString = atob(dataURI.split(",")[1]);
    else byteString = unescape(dataURI.split(",")[1]);

    // separate out the mime component
    var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

    // write the bytes of the string to a typed array
    var ia = new Uint8Array(byteString.length);
    for (var i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }

  blobToBase64(blob) {
    return new Promise((resolve, _reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result.split(",")[1]);
      };
      reader.readAsDataURL(blob);
    });
  }

  submitRecording(event) {
    if (this.validateSignature(event)) {
      this.handleStopMediaRecorder();
    } else {
      window.HSOverlay.close("#modal-loading");
    }
  }

  toggleButton(button) {
    if (button instanceof Event) {
      button = this.nextButtonTarget;
    }

    const checkbox = document.getElementById("tos-checkbox");

    if (checkbox && checkbox.checked) {
      this.enableButton(button);
    } else {
      this.disableButton(button);
    }
  }

  enableButton(button) {
    button.classList.add(
      "bg-forest-600",
      "light:bg-navy-700",
      "text-white",
      "hover:bg-mint-500",
      "hover:text-forest-600",
    );
    button.classList.remove(
      "bg-gray-300",
      "cursor-not-allowed",
      "opacity-50",
      "text-gray-800",
      "hover:bg-gray-400",
      "pointer-events-none",
    );

    button.removeAttribute("aria-disabled");
  }

  disableButton(button) {
    button.classList.add(
      "bg-gray-300",
      "cursor-not-allowed",
      "text-gray-800",
      "hover:bg-gray-400",
      "pointer-events-none",
    );
    button.classList.remove(
      "bg-forest-600",
      "light:bg-navy-700",
      "text-white",
      "hover:bg-mint-500",
      "hover:text-forest-600",
    );

    button.setAttribute("aria-disabled", "true");
  }

  getSignaturePad() {
    const signaturePadElement = document.querySelector("[data-controller='signature_pad']");
    if (signaturePadElement) {
      const signaturePadController = Stimulus.getControllerForElementAndIdentifier(
        signaturePadElement,
        "signature_pad",
      );

      if (signaturePadController) {
        return signaturePadController;
      }
    }
  }

  validateSignature(event) {
    const signaturePad = this.getSignaturePad();
    if (!signaturePad) {
      event.preventDefault();
      sendSentryError("Stat dec: signature pad controller not found during validation");
      return false;
    }
    if (!signaturePad.isSigned()) {
      event.preventDefault();
      const errorEl = signaturePad.element.querySelector(".signature-error");
      if (errorEl) {
        errorEl.classList.remove("hidden");
      }
      signaturePad.canvasTarget.focus();
      return false;
    }
    return true;
  }
}
